import type { Messages } from "@/i18n/locales/en";
import { legalMessagesFr } from "@/i18n/locales/legalFrData";
import { pagesMessagesFr } from "@/i18n/locales/pagesFrData";

export const frMessages = {
  meta: {
    description:
      "WasOK — scannez des produits, enregistrez notes et avis, souvenez-vous de ce qui valait le coup.",
    title: "WasOK — Souvenez-vous de ce qui valait le coup",
  },
  header: {
    navFeatures: "Fonctionnalités",
    navHowItWorks: "Comment ça marche",
    navPrivacy: "Confidentialité",
    navUseCases: "Cas d’usage",
    download: "Télécharger",
    openMenu: "Ouvrir le menu",
    closeMenu: "Fermer le menu",
    languageAria: "Langue",
  },
  hero: {
    badge: "📱 Disponible sur Android",
    title: "Souvenez-vous de ce qui valait le coup.",
    subtitle:
      "Scannez des produits, enregistrez avis et notes et souvenez-vous instantanément si un produit était vraiment bon.",
    googlePlay: "Télécharger sur Google Play",
    watchDemo: "Voir la démo",
    privacyLine: "Toutes les données restent sur votre appareil.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scannez pour vous souvenir",
    snackCard: "Délicieux !",
    coffeeCard: "Trop amer",
    scrollHint: "Faites défiler pour explorer",
  },
  problems: {
    heading: "Ça vous parle ?",
    subheading:
      "On y est tous passés — devant les rayons, à essayer de se souvenir si on avait aimé la dernière fois.",
    notedAgo: "✓ Vous l’avez noté il y a 3 mois",
    items: [
      {
        question: "Quel chocolat j’ai vraiment aimé ?",
        note: "Douceur parfaite",
      },
      {
        question: "Pourquoi j’ai arrêté d’acheter ce shampoing ?",
        note: "Ça desséchait mes cheveux",
      },
      {
        question: "Ce café était bon ou décevant ?",
        note: "Trop amer",
      },
    ],
  },
  howItWorks: {
    heading: "Comment ça marche",
    subheading:
      "Quatre étapes simples pour ne plus oublier votre avis sur les produits.",
    stepPrefix: "Étape",
    steps: [
      {
        title: "Scannez le code-barres de n’importe quel produit",
        description:
          "Rapide et simple avec l’appareil photo. Fonctionne avec tout code-barres.",
      },
      {
        title: "Enregistrez avis, commentaires et photos",
        description:
          "Notez les produits, ajoutez des notes, photos, prix et lieux.",
      },
      {
        title: "Retrouvez votre expérience instantanément",
        description:
          "En rescannant le même produit, voyez votre note et vos notes précédentes.",
      },
      {
        title: "Créez des listes de courses intelligentes",
        description:
          "À partir des produits auxquels vous faites déjà confiance.",
      },
    ],
  },
  privacy: {
    heading: "Votre historique d’achats vous appartient.",
    body: "WasOK vous aide à vous souvenir des produits que vous avez essayés tout en conservant vos évaluations, notes, photos et listes de courses stockées localement sur votre appareil.",
    trustBanner:
      "💚 Conçu autour de votre historique d’achats — pas autour de la collecte de vos données.",
    bullets: [
      "Pas de compte requis",
      "Pas de synchronisation cloud nécessaire",
      "Stockage local prioritaire",
      "Sauvegardes sous votre contrôle",
      "Recherche optionnelle par code-barres pour les informations produit.",
      "Financé par la publicité avec Google AdMob",
    ],
  },
  features: {
    heading: "Tout ce qu’il vous faut",
    subheading:
      "Des fonctionnalités puissantes pour suivre vos produits simplement et en privé.",
    items: [
      {
        title: "Scanner de codes-barres",
        description: "Scan rapide et précis pour tout produit.",
      },
      {
        title: "Notes produits",
        description: "Notez de 1 à 5 étoiles pour vous souvenir de la qualité.",
      },
      {
        title: "Photos produit",
        description: "Ajoutez des photos d’emballage ou de détails.",
      },
      {
        title: "Catégories",
        description: "Organisez vos produits par catégories.",
      },
      {
        title: "Magasin",
        description: "Souvenez-vous où vous avez acheté chaque produit.",
      },
      {
        title: "Prix",
        description: "Suivez les prix pour trouver les meilleures offres.",
      },
      {
        title: "Listes de courses",
        description: "Construisez des listes à partir de vos favoris.",
      },
      {
        title: "Hors ligne",
        description: "Fonctionne parfaitement sans Internet.",
      },
      {
        title: "Stockage local",
        description: "Vos données, en sécurité sur votre appareil.",
      },
    ],
  },
  useCases: {
    heading: "Pour chaque rayon",
    subheading:
      "Alimentation, beauté ou maison — WasOK vous aide à vous souvenir.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & sucreries",
        description: "Souvenez-vous des bonbons qui valaient vraiment le coup.",
      },
      {
        emoji: "☕",
        title: "Café & boissons",
        description: "Repérez les cafés à racheter.",
      },
      {
        emoji: "💄",
        title: "Cosmétique & soins",
        description: "Notes sur les réactions cutanées.",
      },
      {
        emoji: "🧹",
        title: "Articles ménagers",
        description: "Évitez de racheter ce qui a déçu.",
      },
    ],
  },
  screenshots: {
    heading: "Beau & intuitif",
    subheading:
      "Une interface réfléchie qui rend le suivi des produits agréable.",
    items: [
      { title: "Scanner" },
      { title: "Fiche produit" },
      { title: "Notation" },
      { title: "Recherche & historique" },
      { title: "Liste de courses" },
    ],
  },
  cta: {
    heading: "La mémoire des courses pour la vraie vie.",
    body: "Arrêtez d’oublier quels produits valaient le coup. Achetez plus intelligemment dès aujourd’hui.",
    googlePlay: "Télécharger sur Google Play",
    scanQr: "Scannez pour télécharger",
    tagline: "Privé · Hors ligne · Simple",
  },
  footer: {
    tagline: "Souvenez-vous de ce qui valait le coup.",
    linksHeading: "Liens",
    privacyPolicy: "Politique de confidentialité",
    terms: "Conditions d’utilisation",
    contact: "Contact",
    linkFaq: "FAQ",
    linkLicenses: "Licences open source",
    linkAdvertising: "Publicité et monétisation",
    downloadHeading: "Télécharger",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Tous droits réservés. Fait avec 💚 pour mieux acheter.",
  },
  legal: legalMessagesFr,
  faq: pagesMessagesFr.faq,
  support: pagesMessagesFr.support,
  advertising: pagesMessagesFr.advertising,
} satisfies Messages;
